import { type CompareRoute, formatDuration, type QualityTask, ROUTE_LABELS } from '@hoopoe/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { GitCompareArrows } from 'lucide-react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { ApiError, api } from '../lib/api';

// Admin → AI quality (PLAN §7.1, task 20a): which route to use per task. Per task and route: how often the routes
// agreed in Compare mode, how often each was adopted and its items picked, how much reviewers corrected what it
// extracted, latency and cost; then the latest comparisons. Counts only, no document content.

export const Route = createFileRoute('/admin/quality')({ component: QualityPage });

interface Quality {
  tasks: QualityTask[];
  minAdopted: number;
  recent: {
    id: string;
    task: string;
    documentId: string | null;
    filename: string | null;
    routes: CompareRoute[];
    agreementRate: number | null;
    items: number;
    adoptedRoute: CompareRoute | 'mixed' | null;
    mode: 'document' | 'items' | null;
    createdAt: number;
    adoptedAt: number | null;
  }[];
}

const TASK_LABELS: Record<string, string> = {
  extraction: 'Extraction',
  ocr: 'OCR',
  resolution: 'Entity resolution',
  recheck: 'AI re-check',
  chat: 'Chat',
  findings: 'AI findings',
};
const pct = (x: number | null) => (x === null ? '—' : `${Math.round(x * 100)}%`);
const usd = (n: number) => (n > 0 && n < 0.01 ? '< $0.01' : `$${n.toFixed(2)}`);
const adoptedLabel = (r: CompareRoute | 'mixed' | null) => (r === null ? 'Waiting' : r === 'mixed' ? 'Items picked' : ROUTE_LABELS[r]);

function QualityPage() {
  const q = useQuery({ queryKey: ['admin', 'ai', 'quality'], queryFn: () => api<Quality>('/admin/ai/quality') });
  if (q.isError)
    return (
      <p role="alert" className="text-sm text-danger-foreground">
        {q.error instanceof ApiError ? q.error.message : 'The dashboard could not be loaded.'}
      </p>
    );
  if (!q.data) return <PageHeader title="AI quality" description="Loading…" />;
  const { tasks, recent, minAdopted } = q.data;
  const compared = tasks.some(t => t.comparisons > 0);
  return (
    <div className="space-y-6">
      <PageHeader title="AI quality" description="How the AI routes compare per task: agreement, adoption, corrections, time and cost." />
      <Tip id="ai-quality" learnMore="compare-ai-routes">
        Turn on Compare for a task in AI routing, upload a few test documents as standard, and adopt the better run in review. The numbers
        here fill in as you go.
      </Tip>
      {!compared && (
        <EmptyState
          icon={GitCompareArrows}
          title="No comparisons yet"
          learnMore="compare-ai-routes"
          action={
            <Link to="/admin/ai" className="text-sm underline underline-offset-2">
              Open AI routing
            </Link>
          }
        >
          Once documents are read in Compare mode and a run is adopted in review, agreement and adoption show up here.
        </EmptyState>
      )}
      {tasks.map(t => (
        <TaskSection key={t.task} t={t} minAdopted={minAdopted} />
      ))}
      {recent.length > 0 && (
        <section aria-labelledby="recent-title" className="space-y-2">
          <h2 id="recent-title" className="font-semibold">
            Latest comparisons
          </h2>
          <div className="relative overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="p-2 text-start font-medium">
                    Document
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Routes
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Items
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    All agree
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Adopted
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2">
                      {r.documentId && r.filename ? (
                        <Link to="/review/$id" params={{ id: r.documentId }} className="underline underline-offset-2">
                          <DataText>{r.filename}</DataText>
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="p-2">{r.routes.map(x => ROUTE_LABELS[x]).join(' · ')}</td>
                    <td className="p-2">
                      <DataNumber>{r.items}</DataNumber>
                    </td>
                    <td className="p-2">
                      <DataNumber>{pct(r.agreementRate)}</DataNumber>
                    </td>
                    <td className="p-2">{adoptedLabel(r.adoptedRoute)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function TaskSection({ t, minAdopted }: { t: QualityTask; minAdopted: number }) {
  const id = `task-${t.task}`;
  const label = TASK_LABELS[t.task] ?? t.task;
  return (
    <section aria-labelledby={id} className="space-y-3" data-task={t.task}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 id={id} className="font-semibold">
          {label}
        </h2>
        <span className="text-sm text-muted-foreground">
          <DataNumber>{t.comparisons}</DataNumber> {t.comparisons === 1 ? 'comparison' : 'comparisons'} ·{' '}
          <DataNumber>{t.adopted}</DataNumber> adopted · all routes agreed on{' '}
          <DataNumber data-testid="agreement">{pct(t.agreementRate)}</DataNumber> of items
        </span>
      </div>
      <p className="text-sm" data-testid={`suggestion-${t.task}`}>
        {t.suggestion ? (
          <>
            Suggested route: <strong>{t.suggestion.label}</strong>. {t.suggestion.reason}
          </>
        ) : (
          <span className="text-muted-foreground">
            A suggestion appears after <DataNumber>{minAdopted}</DataNumber> adopted comparisons.
          </span>
        )}
      </p>
      <div className="relative overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[48rem] text-sm">
          <caption className="sr-only">{label}: quality per route</caption>
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="p-2 text-start font-medium">
                Route
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                <span className="inline-flex items-center gap-1">
                  Agreement
                  <FieldHelp
                    label="Agreement"
                    help="Share of all compared items this route found exactly as the other routes did. Items it missed or read differently count against it."
                  />
                </span>
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                Adopted
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                <span className="inline-flex items-center gap-1">
                  Items kept
                  <FieldHelp
                    label="Items kept"
                    help="Items kept in review that came from this route, as a share of all items kept (whole runs and items picked one by one)."
                  />
                </span>
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                <span className="inline-flex items-center gap-1">
                  Corrected
                  <FieldHelp label="Corrected" help="Reviewed items from this route that a reviewer edited or rejected." />
                </span>
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                Avg time
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                Cost
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                Runs
              </th>
            </tr>
          </thead>
          <tbody>
            {t.routes.map(r => (
              <tr key={r.route} className="border-t align-top" data-route={r.route}>
                <th scope="row" className="p-2 text-start font-medium">
                  {r.label}
                  {r.models.length > 0 && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      <DataText>{r.models.join(', ')}</DataText>
                    </span>
                  )}
                </th>
                <td className="p-2">
                  <DataNumber>{pct(r.agreement)}</DataNumber>
                  {r.compared > 0 && (
                    <span className="block text-xs text-muted-foreground">
                      in <DataNumber>{r.compared}</DataNumber>
                    </span>
                  )}
                </td>
                <td className="p-2">
                  <DataNumber>{r.adopted}</DataNumber>
                  <span className="block text-xs text-muted-foreground">
                    <DataNumber>{pct(r.adoptionRate)}</DataNumber>
                  </span>
                </td>
                <td className="p-2">
                  <DataNumber>{r.itemPicks}</DataNumber>
                  <span className="block text-xs text-muted-foreground">
                    <DataNumber>{pct(r.pickRate)}</DataNumber>
                  </span>
                </td>
                <td className="p-2">
                  <DataNumber>{pct(r.corrections.rate)}</DataNumber>
                  <span className="block text-xs text-muted-foreground">
                    of <DataNumber>{r.corrections.decided}</DataNumber>
                  </span>
                </td>
                <td className="p-2">
                  <DataNumber>{r.avgLatencyMs === null ? '—' : formatDuration(r.avgLatencyMs)}</DataNumber>
                </td>
                <td className="p-2">
                  <DataNumber>{usd(r.costUsd)}</DataNumber>
                </td>
                <td className="p-2">
                  <DataNumber>{r.runs}</DataNumber>
                  {(r.failed > 0 || r.fallbacks > 0) && (
                    <span className="block text-xs text-muted-foreground">
                      {r.failed > 0 && (
                        <>
                          <DataNumber>{r.failed}</DataNumber> failed{' '}
                        </>
                      )}
                      {r.fallbacks > 0 && (
                        <>
                          <DataNumber>{r.fallbacks}</DataNumber> fallback
                        </>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
